export interface OriginAddResult {
  origins: string[];
  error: string | null;
}

/** Mirrors the server DTO's cap so the form fails here rather than as an opaque 400. */
const MAX_ORIGINS = 50;

/**
 * Allow-list editing rules for a bridge tenant.
 *
 * The allow list is the only thing standing between a public, unauthenticated collection endpoint
 * and a forged event, so a malformed entry is not a cosmetic problem. These rules are the client
 * side of the server's validation: reject anything that is not a bare absolute web origin.
 */
export class BridgeTenantOrigins {
  /** Returns the canonical origin, or null when the value could never match an Origin header. */
  public static normalize(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    let url: URL;
    try {
      url = new URL(trimmed);
    } catch {
      return null;
    }

    // https only: a Shopify storefront is always https, and an http page could not reach the https
    // collector anyway — the browser blocks it as mixed content before the request is sent.
    if (url.protocol !== 'https:') {
      return null;
    }
    // A browser sends scheme://host[:port] and nothing else — a path or query means a typo.
    if ((url.pathname !== '/' && url.pathname !== '') || url.search || url.hash) {
      return null;
    }
    // Wildcards read as a hostname to URL(), so reject them explicitly.
    if (url.hostname.includes('*')) {
      return null;
    }

    return url.origin.toLowerCase();
  }

  public static add(origins: string[], value: string): OriginAddResult {
    const normalized = BridgeTenantOrigins.normalize(value);
    if (!normalized) {
      return { origins, error: 'https:// 로 시작하는 절대 origin이어야 합니다. 경로·와일드카드는 쓸 수 없습니다.' };
    }
    if (origins.includes(normalized)) {
      return { origins, error: '이미 추가된 origin입니다.' };
    }
    if (origins.length >= MAX_ORIGINS) {
      return { origins, error: `origin은 최대 ${MAX_ORIGINS}개까지 등록할 수 있습니다.` };
    }
    return { origins: [...origins, normalized], error: null };
  }

  /** An empty allow list denies every event, so registering one is worse than not registering at all. */
  public static isSubmittable(origins: string[]): boolean {
    return origins.length > 0;
  }
}
